import { useEffect, useRef, useState } from 'react';
import {
  authErrorMessage,
  continueWithGoogle,
  isSilentAuthError,
  resetPassword,
  signInWithEmail,
  signUpWithEmail,
} from './authService';
import { useScrollLock } from '../utils/scrollLock';
import './AuthModal.css';

const COPY = {
  signup: {
    title: 'Join the community',
    sub: 'Create a free account to follow members, share your portfolio and see who holds what.',
    submit: 'Create account',
  },
  signin: {
    title: 'Welcome back',
    sub: 'Sign in to pick up where you left off.',
    submit: 'Sign in',
  },
  reset: {
    title: 'Reset your password',
    sub: 'Enter your email and we’ll send you a link to set a new password.',
    submit: 'Send reset link',
  },
};

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

/**
 * Sign in / sign up popup. Rendered by CommunityAuthProvider; don't mount it yourself.
 * Bottom sheet on phones, centred dialog on larger screens.
 */
export default function AuthModal({ initialMode = 'signup', reason = '', onClose, onAuthenticated }) {
  useScrollLock(); // shared lock: safe when popups overlap
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(null); // 'google' | 'email' | null
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const sheetRef = useRef(null);
  const copy = COPY[mode];

  // Focus the first field, lock page scroll, and give focus back on close.
  useEffect(() => {
    const previous = document.activeElement;
    return () => {
      if (previous && previous.focus) previous.focus();
    };
  }, []);

  useEffect(() => {
    const first = sheetRef.current?.querySelector('input');
    if (first) first.focus();
  }, [mode]);

  // Escape closes the popup wherever focus is (it can drop to <body> while a button is disabled).
  const busyRef = useRef(busy);
  busyRef.current = busy;
  useEffect(() => {
    const onEscape = (e) => {
      if (e.key === 'Escape' && !busyRef.current) onClose();
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [onClose]);

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setNotice('');
  };

  // Keep Tab inside the popup.
  const onKeyDown = (e) => {
    if (e.key !== 'Tab') return;
    const items = [...sheetRef.current.querySelectorAll(FOCUSABLE)];
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const fail = (err) => {
    if (isSilentAuthError(err)) return;
    if (err?.code === 'auth/email-already-in-use') setMode('signin');
    setError(authErrorMessage(err));
  };

  const handleGoogle = async () => {
    setBusy('google');
    setError('');
    try {
      const result = await continueWithGoogle();
      await onAuthenticated(result, 'google');
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');

    if (mode === 'signup' && password.length < 6) {
      setError('Use at least 6 characters for your password.');
      return;
    }

    setBusy('email');
    try {
      if (mode === 'reset') {
        await resetPassword(email);
        setNotice('Check your inbox for a link to reset your password.');
      } else if (mode === 'signup') {
        await onAuthenticated(await signUpWithEmail({ name, email, password }), 'email');
      } else {
        await onAuthenticated(await signInWithEmail({ email, password }), 'email');
      }
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="auth-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        className="auth-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-title"
        aria-describedby="auth-sub"
        ref={sheetRef}
        onKeyDown={onKeyDown}
      >
        <button type="button" className="auth-close" aria-label="Close" onClick={onClose} disabled={Boolean(busy)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        <div className="auth-head">
          <span className="auth-brand" aria-hidden="true">
            WInvest<span>COLLECTIVE</span>
          </span>
          <h2 id="auth-title">{copy.title}</h2>
          <p id="auth-sub" className="auth-sub">
            {mode === 'signup' && reason ? reason : copy.sub}
          </p>
        </div>

        {mode !== 'reset' && (
          <>
            <button type="button" className="auth-google" onClick={handleGoogle} disabled={Boolean(busy)}>
              <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
                <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
              </svg>
              {busy === 'google' ? 'Opening Google…' : 'Continue with Google'}
            </button>
            <div className="auth-divider" role="presentation">
              <span>or use your email</span>
            </div>
          </>
        )}

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          {mode === 'signup' && (
            <div className="auth-field">
              <label htmlFor="auth-name">Name</label>
              <input id="auth-name" type="text" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          )}

          <div className="auth-field">
            <label htmlFor="auth-email">Email</label>
            <input
              id="auth-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@example.com"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {mode !== 'reset' && (
            <div className="auth-field">
              <div className="auth-label-row">
                <label htmlFor="auth-password">Password</label>
                {mode === 'signin' && (
                  <button type="button" className="auth-link" onClick={() => switchMode('reset')}>
                    Forgot password?
                  </button>
                )}
              </div>
              <input
                id="auth-password"
                type="password"
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {mode === 'signup' && <span className="auth-hint">At least 6 characters.</span>}
            </div>
          )}

          {error && <p className="auth-error" role="alert">{error}</p>}
          {notice && <p className="auth-notice" role="status">{notice}</p>}

          <button type="submit" className="auth-submit" disabled={Boolean(busy)}>
            {busy === 'email' ? 'Please wait…' : copy.submit}
          </button>
        </form>

        <p className="auth-switch">
          {mode === 'signup' && (
            <>Already a member? <button type="button" className="auth-link" onClick={() => switchMode('signin')}>Sign in</button></>
          )}
          {mode === 'signin' && (
            <>New here? <button type="button" className="auth-link" onClick={() => switchMode('signup')}>Create an account</button></>
          )}
          {mode === 'reset' && (
            <button type="button" className="auth-link" onClick={() => switchMode('signin')}>Back to sign in</button>
          )}
        </p>

        <p className="auth-small">For educational purposes only. Nothing here is financial advice.</p>
      </div>
    </div>
  );
}
